import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import "@fontsource/playfair-display/700.css";
import LoginForm from "@/components/forms/loginForm/LoginForm";
import { ButtonClose } from "@/components/ui";
import { Link } from "react-router-dom";
import { SvgWhatsapp } from "@/icons";

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const EMPTY_TIME: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

interface Props {
  launchDate: Date; // ahora es obligatorio recibir la fecha
  nowServer?: Date;
}

const PreLaunchPage = ({ launchDate }: Props) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(EMPTY_TIME);
  const [showTitle, setShowTitle] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  /*  const location = useLocation(); */

  // ⏳ Calcular tiempo restante
  const calculateTimeLeft = (): TimeLeft => {
    const diff = launchDate.getTime() - Date.now();
    if (diff <= 0) return EMPTY_TIME;

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  /*   useEffect(() => {
      setShowLoginModal(false);
    }, [location.pathname]);
     */

  // ⏱️ Intervalo del contador
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [launchDate]);

  // 🔊 Audio (solo intenta reproducir, sin romper UX)
  // 🔊 Audio (no bloquea la intro si el navegador lo bloquea)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.8;

    // Mostrar la intro siempre
    const introTimer = setTimeout(() => {
      setShowTitle(true);
    }, 800);

    // Intentar reproducir audio
    audio.play().catch(() => {
      // Si el navegador bloquea el audio, se activará al primer gesto
      const unlockAudio = () => {
        audio.play().catch(() => { });
      };

      window.addEventListener("pointerdown", unlockAudio, { once: true });
    });

    return () => clearTimeout(introTimer);

  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070b14] text-white relative overflow-hidden px-6">

      {/* 🌌 Glow fondo suave */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.15),transparent_70%)] blur-3xl" />

      {/* 🎵 Audio */}
      <audio ref={audioRef} src="/sonidos/entradaCuentaAtras.m4a" preload="auto" />

      <div className="relative z-10 text-center max-w-xl w-full">

        {/* LOGO */}
        {showTitle && (
          <motion.h1
            initial={{ opacity: 0, y: 50, scale: 0.9, filter: "blur(8px)" }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              filter: "blur(0px)",
            }}
            transition={{
              type: "spring",        // rebote natural
              stiffness: 180,        // más rigidez (más rebote)
              damping: 12,           // menos amortiguado
              mass: 0.8,             // ligero
              duration: 1,         // más rápido
            }}
            className="relative text-6xl sm:text-7xl tracking-wide mb-6 font-[Playfair_Display] text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-400 to-blue-500"
            style={{
              textShadow: `
                  1px 1px 2px rgba(0,0,0,0.6),
                  0 0 5px rgba(59,130,246,0.5),
                  0 0 15px rgba(59,130,246,0.6),
                  0 0 25px rgba(37,99,235,0.4)
                `,
              WebkitTextStroke: "1px rgba(255,255,255,0.15)",
            }}
          >
            LinkServs
          </motion.h1>
        )}

        {showTitle && (
          <>
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.02 }}
              className="text-sm md:text-lg font-semibold text-center leading-tight mb-2"
            >
              Consigue el 50% de descuento. Pincha en{" "}
              <span className="text-green-400 font-bold">
                WhatsApp
              </span>
            </motion.h3>

            <motion.h3
              className="text-base md:text-lg font-semibold mt-2 flex items-center justify-center gap-3 mb-2"
              animate={{
                textShadow: [
                  "0 0 10px rgba(59,130,246,0.25)",
                  "0 0 12px rgba(59,130,246,0.35)",
                  "0 0 10px rgba(59,130,246,0.25)"
                ]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* FIJO — SIN PARPADEO */}
              <span className="text-blue-300">
                Fijo: 925 004 105
              </span>

              <span className="text-zinc-600">|</span>

              {/* WHATSAPP */}
              <a
                href="https://wa.me/34671562558?text=Quiero%20mi%20cupón."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-0.5 rounded-full 
          border border-green-400/40 
          bg-green-500/10 
          shadow-[0_0_10px_rgba(34,197,94,0.2)]
          hover:shadow-[0_0_20px_rgba(34,197,94,0.5)]
          transition-all duration-500"
              >
                <SvgWhatsapp
                  className="text-green-400 drop-shadow-[0_0_6px_rgba(34,197,94,0.7)]"
                  height={18}
                  width={18}
                />

                <span className="text-blue-300 font-medium text-sm leading-none">
                  671 562 558
                </span>
              </a>
            </motion.h3>
          </>
        )}


        {/* TITULO */}
        {showTitle && (
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.02 }}
            className="text-md md:text-2xl font-bold leading-tight mb-6"
          >
            DESTACA O SIGUE
            SIENDO <motion.span
              className="text-blue-300 inline-block"
              animate={{
                opacity: [1, 0, 1],
                y: [0, 0, 0],
                filter: ["blur(0px)", "blur(6px)", "blur(0px)"]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              INVISIBLE
            </motion.span>
          </motion.h2>
        )}

        {/* TEXTO */}
        {showTitle && (
          <p className="text-zinc-400 mb-2">
            🚀 Accede antes del lanzamiento y consigue <br />
            visibilidad desde el primer día.
          </p>
        )}

        {/* WARNING */}
        {showTitle && (
          <p className="text-yellow-400 mb-6">
            ⚠️ Plazas limitadas para los primeros profesionales
          </p>
        )}

        {/* SEPARADOR */}
        {showTitle && (
          <div className="flex items-center justify-center gap-4 mb-6 text-zinc-500">
            <div className="h-[1px] w-16 bg-zinc-700" />
            <span className="text-sm">⏳ Lanzamiento en curso</span>
            <div className="h-[1px] w-16 bg-zinc-700" />
          </div>
        )}

        {/* CONTADOR (SIN CARDS) */}
        {showTitle && (
          <div className="flex justify-center gap-8 mb-10">
            {["days", "hours", "minutes", "seconds"].map((key, i) => {
              const labels = ["Días", "Horas", "Min", "Seg"];
              const value = timeLeft[key as keyof typeof timeLeft];

              return (
                <div key={key} className="text-center">
                  <div
                    className="text-4xl sm:text-5xl font-semibold text-blue-300"
                    style={{
                      textShadow: "0 0 12px rgba(59,130,246,0.8)"
                    }}
                  >
                    {value.toString().padStart(2, "0")}
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">
                    {labels[i]}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* BOTÓN PRINCIPAL (ESTILO BORDE GLOW) */}
        {showTitle && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-6"
          >
            <Link
              to="/suscribirse"
              className="px-10 py-4 rounded-xl font-semibold
            border border-blue-400 text-white
            shadow-[0_0_25px_rgba(59,130,246,0.4)]
            hover:shadow-[0_0_40px_rgba(59,130,246,0.8)]
            transition-all duration-300 inline-block"
            >
              QUIERO SER DE LOS PRIMEROS
            </Link>
          </motion.div>
        )}

        {/* INFO */}
        {showTitle && (
          <p className="text-zinc-500 text-sm mb-6">
            🔒 Acceso anticipado solo para profesionales seleccionados
          </p>
        )}

        {/* BENEFICIOS */}
        {showTitle && (
          <div className="text-left max-w-xs mx-auto mb-8 space-y-2 text-zinc-300">
            <p>✅ Más visibilidad</p>
            <p>✅ Más clientes</p>
            <p>✅ Sin depender del boca a boca</p>
          </div>
        )}

        {/* BOTÓN VERDE */}
        {showTitle && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowLoginModal(true)}
            className="px-10 py-3 rounded-xl font-semibold
          border border-green-400 text-green-300
          shadow-[0_0_20px_rgba(34,197,94,0.4)]
          hover:shadow-[0_0_35px_rgba(34,197,94,0.8)]
          transition-all duration-300"
          >
            YA TENGO MI ACCESO
          </motion.button>
        )}

        {/* MODAL */}
        {showLoginModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#0b1220] p-6 rounded-xl w-full max-w-md relative border border-white/10">

              <ButtonClose
                onClick={() => setShowLoginModal(false)}
                className="absolute top-2 right-2"
              />

              <LoginForm onLoginSuccess={() => setShowLoginModal(false)} />
            </div>
          </div>
        )}

        {/* FOOTER */}
        {showTitle && (
          <p className="text-xs text-zinc-500 mt-10 mb-4">
            LinkServs © {new Date().getFullYear()} — Todos los derechos reservados
          </p>
        )}

      </div>
    </div >
  );
};

export default PreLaunchPage;
